import { AfterViewInit, Directive, OnDestroy, Optional } from '@angular/core';
import { Popover } from 'primeng/popover';
import { Dialog } from 'primeng/dialog';

/**
 * Generic overlay guards for PrimeNG overlays (Popover, Dialog).
 * - Only the topmost overlay responds to ESC / window resize
 * - For Popover, clicks inside other overlays won't close the underlying popover
 *
 * Usage:
 *   <p-popover overlayGuards></p-popover>
 *   <p-dialog overlayGuards></p-dialog>
 */
@Directive({
  selector: '[overlayGuards]'
})
export class OverlayGuardsDirective implements AfterViewInit, OnDestroy {
  private restoreEsc?: () => void;
  private restoreResize?: () => void;
  private unlistenClickCapture?: () => void;
  private unlistenTouchCapture?: () => void;

  constructor(
    @Optional() private popover: Popover,
    @Optional() private dialog: Dialog
  ) {}

  private get overlayInstance(): any | null {
    return this.popover ?? this.dialog ?? null;
  }

  ngAfterViewInit(): void {
    const overlay = this.overlayInstance;
    if (!overlay) {
      // Using console.warn here is intentional for dev visibility; safe in prod as no-op
      console.warn('overlayGuards used outside of a Popover/Dialog instance');
      return;
    }

    this.patchEscapeGuard(overlay);
    this.patchResizeGuard(overlay);
    this.attachCrossOverlayClickGuards();
  }

  ngOnDestroy(): void {
    this.restoreEsc?.();
    this.restoreResize?.();
    this.unlistenClickCapture?.();
    this.unlistenTouchCapture?.();
  }

  // --- Guards ---------------------------------------------------------------

  private patchEscapeGuard(overlay: any) {
    const originalHandler = overlay.onEscapeKeydown?.bind(overlay);
    if (!originalHandler) return; // Dialog may not expose this; only patch if present

    overlay.onEscapeKeydown = (event: KeyboardEvent) => {
      const top = this.getTopMostOverlay() ?? this.getClosestOverlay(document.activeElement as Element);
      if (top && overlay?.container && top !== overlay.container) {
        // Another overlay is top-most; ignore ESC for this overlay
        return;
      }
      originalHandler(event);
    };

    this.restoreEsc = () => {
      overlay.onEscapeKeydown = originalHandler;
    };
  }

  private patchResizeGuard(overlay: any) {
    const originalHandler = overlay.onWindowResize?.bind(overlay);
    if (!originalHandler) return; // Only patch if present (Popover exposes it)

    overlay.onWindowResize = () => {
      const top = this.getTopMostOverlay() ?? this.getClosestOverlay(document.activeElement as Element);
      if (top && overlay?.container && top !== overlay.container) {
        // Another overlay is top-most; ignore resize for this overlay
        return;
      }
      originalHandler();
    };

    this.restoreResize = () => {
      overlay.onWindowResize = originalHandler;
    };
  }

  /**
   * For Popover, prevent outside-click close when the click originates inside another overlay.
   * Achieved by setting selfClick=true in capture phase if the event target's overlay root
   * differs from this popover's container.
   */
  private attachCrossOverlayClickGuards() {
    if (!this.popover) return; // Only relevant to Popover behavior

    const capture = (ev: Event) => {
      const eventTarget = ev.target as Element | null;
      const top = this.getTopMostOverlay() ?? this.getClosestOverlay(eventTarget);
      const thisContainer = this.popover!.container as HTMLElement | undefined;
      if (top && thisContainer && top !== thisContainer) {
        // Another overlay is top-most; mark as selfClick so Popover logic returns early and does not hide
        this.popover!.selfClick = true;
      }
    };

    document.addEventListener('click', capture, true);
    document.addEventListener('touchstart', capture, true);

    this.unlistenClickCapture = () => document.removeEventListener('click', capture, true);
    this.unlistenTouchCapture = () => document.removeEventListener('touchstart', capture, true);
  }

  // --- DOM helpers ----------------------------------------------------------

  private getClosestOverlay(element: Element | null): HTMLElement | null {
    const selector = '[role="dialog"], .p-dialog, .p-popover, .p-overlay-mask, .p-dialog-mask';
    return (element && element.closest(selector)) as HTMLElement | null;
  }

  private getTopMostOverlay(): HTMLElement | null {
    let top: HTMLElement | null = null;
    let maxZIndex = -1;
    document.querySelectorAll('[role="dialog"], .p-popover').forEach((node) => {
      const z = +(getComputedStyle(node as HTMLElement).zIndex || '0') || 0;
      if (z > maxZIndex) {
        maxZIndex = z;
        top = node as HTMLElement;
      }
    });
    return top;
  }
}

